import type { APIResponse } from '@yourownmetric/shared'

import { apiSlice } from './apiSlice'

export interface TenantData {
    id: string
    name: string
    slug: string
    plan: string
    status: string
    region: string
    country: string
    settings: string // JSON-encoded TenantSettings
    createdAt: string
}

interface UpdateTenantPayload {
    tenantId: string
    name?: string
    country?: string
    settings?: {
        timezone?: string
        dataRetentionDays?: number
        ipAnonymization?: 'hash' | 'truncate' | 'discard'
        cookieless?: boolean
        botFiltering?: boolean
    }
}

/** GET /v1/tenants/:id/deletion (lifecycle/deletion.ts): the org's latest closure request. */
export interface OrgDeletion {
    id: string
    status: 'scheduled' | 'purging' | 'completed' | 'cancelled'
    requestedAt: string
    purgeAfter: string
    deadline: string
    completedAt: string | null
    certificateSha256: string | null
    certificate: unknown
}

export const settingsApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getTenant: builder.query<TenantData, string>({
            query: tenantId => `/tenants/${tenantId}`,
            transformResponse: (res: APIResponse<TenantData>) => res.data!,
            providesTags: ['Tenants'],
        }),

        updateTenant: builder.mutation<{ id: string; updated: boolean }, UpdateTenantPayload>({
            query: ({ tenantId, ...body }) => ({
                url: `/tenants/${tenantId}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: APIResponse<{ id: string; updated: boolean }>) => res.data!,
            invalidatesTags: ['Tenants'],
        }),

        getOrgDeletion: builder.query<OrgDeletion | null, string>({
            query: tenantId => `/tenants/${tenantId}/deletion`,
            transformResponse: (res: APIResponse<OrgDeletion | null>) => res.data ?? null,
            providesTags: ['Tenants'],
        }),
        closeOrganisation: builder.mutation<{ deletion: OrgDeletion }, string>({
            query: tenantId => ({ url: `/tenants/${tenantId}`, method: 'DELETE' }),
            transformResponse: (res: APIResponse<{ deletion: OrgDeletion }>) => res.data!,
            invalidatesTags: ['Tenants'],
        }),
        restoreOrganisation: builder.mutation<OrgDeletion, string>({
            query: tenantId => ({ url: `/tenants/${tenantId}/restore`, method: 'POST' }),
            transformResponse: (res: APIResponse<OrgDeletion>) => res.data!,
            invalidatesTags: ['Tenants'],
        }),
    }),
})

export const {
    useGetTenantQuery,
    useUpdateTenantMutation,
    useGetOrgDeletionQuery,
    useCloseOrganisationMutation,
    useRestoreOrganisationMutation,
} = settingsApi
